import React, { useState, useEffect } from 'react';
import { Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, Play, Clock, Loader, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';



const ScraperMonitor = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingScraper, setTestingScraper] = useState(null);
  const [resettingScraper, setResettingScraper] = useState(null);
  const [expandedScraper, setExpandedScraper] = useState(null);
  const [history, setHistory] = useState({});
  
  const scraperDescriptions = {
    'bank_of_canada': 'Bank of Canada policy rate',
    'ratehub': 'RateHub.ca mortgage rates',
    'wowa': 'WOWA.ca economic indicators',
    'td_incentives': 'TD Bank incentives',
    'rbc_incentives': 'RBC incentives',
    'bmo_incentives': 'BMO incentives',
    'scotiabank_incentives': 'Scotiabank incentives',
    'cibc_incentives': 'CIBC incentives'
  };
  
  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);
  
  const loadStatus = async () => {
    try {
      const response = await api.get(`/api/admin/scraper-status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading scraper status:', error);
      toast.error('Failed to load scraper status');
    } finally {
      setLoading(false);
    }
  };
  
  const loadHistory = async (scraperName) => {
    try {
      const response = await api.get(`/api/admin/scraper-history/${scraperName}`);
      setHistory(prev => ({ ...prev, [scraperName]: response.data.history }));
    } catch (error) {
      console.error('Error loading scraper history:', error);
    }
  };
  
  const testScraper = async (scraperName) => {
    setTestingScraper(scraperName);
    try {
      const response = await api.post(`/api/admin/scraper-test/${scraperName}`, {});
      
      if (response.data.success) {
        toast.success(`${scraperName} test passed!`);
        loadStatus();
      } else {
        toast.error(`${scraperName} test failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Scraper test error:', error);
      toast.error(error.response?.data?.detail || 'Scraper test failed');
    } finally {
      setTestingScraper(null);
    }
  };
  
  const resetScraper = async (scraperName) => {
    setResettingScraper(scraperName);
    try {
      await api.post(`/api/admin/scraper-reset/${scraperName}`, {});
      toast.success(`${scraperName} metrics reset`);
      loadStatus();
    } catch (error) {
      console.error('Scraper reset error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset scraper');
    } finally {
      setResettingScraper(null);
    }
  };
  
  const toggleExpand = (scraperName) => {
    if (expandedScraper === scraperName) {
      setExpandedScraper(null);
    } else {
      setExpandedScraper(scraperName);
      if (!history[scraperName]) {
        loadHistory(scraperName);
      }
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-400 bg-green-400/10';
      case 'degraded': return 'text-yellow-400 bg-yellow-400/10';
      case 'failing': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4" />;
      case 'degraded': return <AlertTriangle className="h-4 w-4" />;
      case 'failing': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };
  
  const getOverallStatusBadge = (overall) => {
    const colors = {
      'healthy': 'bg-green-500/20 text-green-400 border-green-500/30',
      'warning': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'unknown': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[overall] || colors.unknown;
  };
  
  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  
  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div data-testid="scraper-monitor" className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold flex items-center gap-2 mb-2">
          <Activity className="h-6 w-6 text-primary" />
          Scraper Monitor
        </h2>
        <p className="text-muted-foreground">
          Monitor the health and status of all web scrapers. Get alerts when scrapers fail.
        </p>
      </div>
      
      {/* Overall Status */}
      {status && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getOverallStatusBadge(status.overall_status)}`}>
                {status.overall_status?.toUpperCase()}
              </span>
              <span className="text-sm text-muted-foreground">
                Last checked: {formatTime(status.last_check)}
              </span>
            </div>
            <button
              onClick={loadStatus}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="Refresh status"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-background rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{status.summary?.healthy || 0}</p>
              <p className="text-xs text-muted-foreground">Healthy</p>
            </div>
            <div className="bg-background rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">{status.summary?.degraded || 0}</p>
              <p className="text-xs text-muted-foreground">Degraded</p>
            </div>
            <div className="bg-background rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{status.summary?.failing || 0}</p>
              <p className="text-xs text-muted-foreground">Failing</p>
            </div>
            <div className="bg-background rounded-lg p-4 text-center">
              <p className="text-2xl font-bold">{status.summary?.total_scrapers || 0}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Individual Scrapers */}
      <div className="space-y-3">
        <h3 className="text-lg font-heading font-semibold">Scrapers</h3>
        
        {status?.scrapers && Object.entries(status.scrapers).map(([name, metrics]) => (
          <div key={name} className="bg-card border border-border rounded-xl overflow-hidden">
            <div 
              className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleExpand(name)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getStatusColor(metrics.status)}`}>
                    {getStatusIcon(metrics.status)}
                  </div>
                  <div>
                    <h4 className="font-medium">{name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                    <p className="text-xs text-muted-foreground">{scraperDescriptions[name] || name}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Quick Stats */}
                  <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
                    <div>
                      <span className="text-foreground font-medium">{metrics.successful_runs}</span>
                      <span className="text-green-400"> / </span>
                      <span>{metrics.total_runs}</span>
                      <span className="ml-1">runs</span>
                    </div>
                    <div>
                      Avg: {formatDuration(metrics.avg_run_duration_ms)}
                    </div>
                    {metrics.consecutive_failures > 0 && (
                      <div className="text-red-400">
                        {metrics.consecutive_failures} consecutive failures
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => testScraper(name)}
                      disabled={testingScraper === name}
                      className="p-2 bg-background border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
                      title="Test scraper"
                    >
                      {testingScraper === name ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => resetScraper(name)}
                      disabled={resettingScraper === name}
                      className="p-2 bg-background border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
                      title="Reset metrics"
                    >
                      {resettingScraper === name ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  
                  {expandedScraper === name ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </div>
            
            {/* Expanded Details */}
            {expandedScraper === name && (
              <div className="border-t border-border p-4 bg-background/50 space-y-4">
                {/* Detailed Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Status</p>
                    <p className={`font-medium ${getStatusColor(metrics.status).split(' ')[0]}`}>
                      {metrics.status?.toUpperCase()}
                    </p>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Success Rate</p>
                    <p className="font-medium">
                      {metrics.total_runs > 0 
                        ? `${((metrics.successful_runs / metrics.total_runs) * 100).toFixed(1)}%`
                        : 'N/A'
                      }
                    </p>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Last Success</p>
                    <p className="font-medium text-sm">{formatTime(metrics.last_success)}</p>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Data Freshness</p>
                    <p className={`font-medium ${metrics.data_freshness === 'fresh' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {metrics.data_freshness?.toUpperCase()}
                    </p>
                  </div>
                </div>
                
                {/* Last Error */}
                {metrics.last_error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-xs text-red-400 mb-1">Last Error:</p>
                    <p className="text-sm text-red-300 font-mono break-all">{metrics.last_error}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      At: {formatTime(metrics.last_failure)}
                    </p>
                  </div>
                )}
                
                {/* Run History */}
                {history[name] && history[name].length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium mb-2">Recent Runs</h5>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {history[name].slice(-10).reverse().map((run, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between text-xs p-2 rounded ${
                            run.success ? 'bg-green-500/10' : 'bg-red-500/10'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {run.success ? (
                              <CheckCircle className="h-3 w-3 text-green-400" />
                            ) : (
                              <XCircle className="h-3 w-3 text-red-400" />
                            )}
                            <span className="text-muted-foreground">
                              {new Date(run.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-muted-foreground">
                            <span>{formatDuration(run.duration_ms)}</span>
                            {run.data_items > 0 && (
                              <span>{run.data_items} items</span>
                            )}
                            {run.error && (
                              <span className="text-red-400 max-w-[200px] truncate" title={run.error}>
                                {run.error}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Help Section */}
      <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
        <h4 className="text-sm font-semibold mb-2">Scraper Status Guide</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
            <div>
              <p className="font-medium text-green-400">Healthy</p>
              <p className="text-muted-foreground">Scraper is working normally</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-400">Degraded</p>
              <p className="text-muted-foreground">2+ consecutive failures</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-red-400 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Failing</p>
              <p className="text-muted-foreground">5+ failures, admin alerted</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScraperMonitor;
